import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, FloppyDisk, Palette, TextT } from '@phosphor-icons/react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const FONTS = ['Playfair Display', 'Inter', 'Georgia', 'Courier New', 'Arial Black'];

const InviteEditor = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [event, setEvent] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [config, setConfig] = useState({
    template_id: 'elegant-dark',
    event_name: '', event_date: '', location: '',
    custom_message: '', rsvp_options: 'yes_no',
    font: 'Playfair Display', accent: '#D4AF37',
  });

  useEffect(() => {
    (async () => {
      try {
        const [evRes, tRes] = await Promise.all([
          axios.get(`${API}/events/slug/${slug}`),
          axios.get(`${API}/invite-templates`),
        ]);
        setEvent(evRes.data);
        setTemplates(tRes.data);
        setConfig(prev => ({ ...prev, event_name: evRes.data.event_name || '', event_date: evRes.data.event_date || '' }));
        // Load saved config
        try {
          const saved = await axios.get(`${API}/events/${evRes.data.event_id}/template`);
          if (saved.data?.invite_config) setConfig(prev => ({ ...prev, ...saved.data.invite_config }));
        } catch {}
      } catch { navigate(-1); }
    })();
  }, [slug]);

  const tpl = templates.find(t => t.id === config.template_id) || templates[0] || {};

  const save = async () => {
    if (!event) return;
    try {
      // Save invite config alongside template config
      const existing = await axios.get(`${API}/events/${event.event_id}/template`).then(r => r.data).catch(() => ({}));
      await axios.put(`${API}/events/${event.event_id}/template`, {
        template_config: { ...existing, invite_config: config }
      });
      toast.success('Invite design saved');
    } catch { toast.error('Save failed'); }
  };

  const update = (key, val) => setConfig(prev => ({ ...prev, [key]: val }));

  if (!event) return <div className="min-h-screen bg-[#15161B] flex items-center justify-center"><div className="animate-spin rounded-full h-10 w-10 border-4 border-[#B89762]/50 border-t-transparent" /></div>;

  return (
    <div className="min-h-screen" style={{ background: '#15161B', fontFamily: 'Inter, sans-serif' }} data-testid="invite-editor">
      {/* Header */}
      <div className="flex items-center justify-between px-6 py-4 border-b border-white/10">
        <button onClick={() => navigate(`/event/${slug}/invites`)} className="flex items-center gap-2 text-white/60 hover:text-white transition-colors" data-testid="editor-back">
          <ArrowLeft size={20} weight="bold" /><span className="text-sm font-medium">Back to Invites</span>
        </button>
        <h1 className="text-white font-bold text-lg">Invite Designer</h1>
        <button onClick={save} className="flex items-center gap-2 px-5 py-2 rounded-xl font-bold text-white text-sm hover:scale-105 transition-all" style={{ background: 'linear-gradient(135deg, #B89762, #C29D52)' }} data-testid="save-invite">
          <FloppyDisk size={16} weight="bold" /> Save
        </button>
      </div>

      <div className="flex flex-col lg:flex-row lg:h-[calc(100vh-65px)]">
        {/* LEFT: Live Preview — on mobile it gets natural content height so
             the full invite card is visible before the settings panel; on
             desktop it's a scrollable column alongside the settings. */}
        <div className="flex-1 flex items-center justify-center p-8 lg:overflow-auto" style={{ background: 'rgba(0,0,0,0.3)' }}>
          <div className="rounded-2xl overflow-hidden w-full max-w-sm" style={{ boxShadow: '0 25px 80px rgba(0,0,0,0.5)' }} data-testid="invite-preview">
            <div className="aspect-[3/4] flex flex-col items-center justify-center p-10 text-center"
              style={{ background: tpl.bg?.startsWith('linear') ? tpl.bg : (tpl.bg || '#1a1a2e'), color: tpl.text_color || '#FFFFFF' }}>
              <p className="text-sm opacity-50 mb-4">You're Invited</p>
              <h2 className="text-3xl font-bold mb-2" style={{ fontFamily: config.font }}>{config.event_name || 'Event Name'}</h2>
              {config.event_date && <p className="text-base opacity-60 mb-1">{config.event_date}</p>}
              {config.location && <p className="text-sm opacity-50 mb-4">{config.location}</p>}
              {config.custom_message && <p className="text-sm opacity-70 italic mt-4 mb-4 max-w-xs">{config.custom_message}</p>}
              <div className="px-8 py-3 rounded-full text-sm font-bold mt-4" style={{ background: config.accent || tpl.accent, color: 'white' }}>
                RSVP Now
              </div>
              <p className="text-xs opacity-30 mt-8">Powered by EventDrop</p>
            </div>
          </div>
        </div>

        {/* RIGHT: Settings Panel */}
        <div className="lg:w-96 p-6 border-t lg:border-t-0 lg:border-l border-white/10 lg:overflow-y-auto" data-testid="invite-settings">
          {/* Template Selector */}
          <div className="mb-6">
            <label className="block text-xs text-white/40 font-semibold mb-2 uppercase tracking-wider">Template</label>
            <div className="grid grid-cols-4 gap-2">
              {templates.map(t => (
                <button key={t.id} onClick={() => { update('template_id', t.id); update('accent', t.accent); }}
                  className={`aspect-[3/4] rounded-lg overflow-hidden border-2 transition-all ${config.template_id === t.id ? 'border-[#B89762]/50' : 'border-white/10 hover:border-white/20'}`}
                  style={{ background: t.bg?.startsWith('linear') ? t.bg : t.bg }}
                  data-testid={`tpl-${t.id}`} />
              ))}
            </div>
          </div>

          {/* Event Details */}
          <div className="space-y-4 mb-6">
            <div>
              <label className="block text-xs text-white/40 font-semibold mb-1">Event Name</label>
              <input value={config.event_name} onChange={e => update('event_name', e.target.value)}
                className="w-full px-4 py-3 rounded-xl bg-white/10 border border-white/10 text-white text-sm focus:outline-none focus:border-[#B89762]/50" data-testid="invite-event-name" />
            </div>
            <div>
              <label className="block text-xs text-white/40 font-semibold mb-1">Date</label>
              <input type="date" value={config.event_date} onChange={e => update('event_date', e.target.value)}
                className="w-full px-4 py-3 rounded-xl bg-white/10 border border-white/10 text-white text-sm focus:outline-none focus:border-[#B89762]/50" data-testid="invite-date" />
            </div>
            <div>
              <label className="block text-xs text-white/40 font-semibold mb-1">Location</label>
              <input value={config.location} onChange={e => update('location', e.target.value)} placeholder="Venue name & address"
                className="w-full px-4 py-3 rounded-xl bg-white/10 border border-white/10 text-white text-sm placeholder-white/20 focus:outline-none focus:border-[#B89762]/50" data-testid="invite-location" />
            </div>
            <div>
              <label className="block text-xs text-white/40 font-semibold mb-1">Personal Message</label>
              <textarea value={config.custom_message} onChange={e => update('custom_message', e.target.value)} placeholder="Add a note..." rows={3}
                className="w-full px-4 py-3 rounded-xl bg-white/10 border border-white/10 text-white text-sm placeholder-white/20 focus:outline-none focus:border-[#B89762]/50 resize-none" data-testid="invite-message" />
            </div>
          </div>

          {/* Design Controls */}
          <div className="space-y-4 mb-6">
            <div>
              <label className="block text-xs text-white/40 font-semibold mb-1 flex items-center gap-1"><TextT size={12} weight="bold" /> Font</label>
              <select value={config.font} onChange={e => update('font', e.target.value)}
                className="w-full px-4 py-3 rounded-xl bg-white/10 border border-white/10 text-white text-sm" data-testid="invite-font">
                {FONTS.map(f => <option key={f} value={f} style={{ color: '#000' }}>{f}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs text-white/40 font-semibold mb-1 flex items-center gap-1"><Palette size={12} weight="bold" /> Accent Color</label>
              <div className="flex items-center gap-3">
                <input type="color" value={config.accent} onChange={e => update('accent', e.target.value)}
                  className="w-10 h-10 rounded-xl border-0 cursor-pointer" data-testid="invite-accent" />
                <div className="flex gap-1.5">
                  {['#D4AF37', '#A033D8', '#B89762', '#10B981', '#C29D52', '#3B82F6'].map(c => (
                    <button key={c} onClick={() => update('accent', c)}
                      className="w-7 h-7 rounded-full border-2 transition-all hover:scale-110"
                      style={{ background: c, borderColor: config.accent === c ? '#FFF' : 'transparent' }} />
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* RSVP Options */}
          <div className="mb-6">
            <label className="block text-xs text-white/40 font-semibold mb-2">RSVP Type</label>
            <div className="space-y-2">
              {[
                { id: 'yes_no', label: 'Yes / No' },
                { id: 'yes_no_plus', label: 'Yes / No / +1' },
                { id: 'yes_no_meal', label: 'Yes / No / Meal Choice' },
              ].map(opt => (
                <label key={opt.id} className={`flex items-center gap-3 px-4 py-3 rounded-xl cursor-pointer transition-all ${config.rsvp_options === opt.id ? 'bg-[#B89762]/10 border border-[#B89762]/50/30' : 'bg-[#15161B] border border-white/5'}`}>
                  <input type="radio" name="rsvp" value={opt.id} checked={config.rsvp_options === opt.id}
                    onChange={() => update('rsvp_options', opt.id)} className="accent-[#B89762]" />
                  <span className="text-white text-sm font-medium">{opt.label}</span>
                </label>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InviteEditor;
